import {
	Answer,
	ComplaintDocument,
	MultipleChoiceOptionPDF,
	UserComplaint,
} from '@/interfaces';
import {
	Document,
	Text,
	Page,
	StyleSheet,
	View,
	Font,
} from '@react-pdf/renderer';

interface Props {
	tableData?: ComplaintDocument;
	caseName?: string;
}

Font.register({
	family: 'Poppins-SemiBold',
	src: '/fonts/Poppins-SemiBold.ttf',
});

const styles = StyleSheet.create({
	page: {
		padding: 30,
	},
	text: {
		color: '#000',
		fontSize: 11,
		fontWeight: 400,
	},
	subTitle: {
		color: '#000',
		fontSize: 11,
		fontWeight: 600,
		fontFamily: 'Poppins-SemiBold',
	},
	title: {
		color: '#000',
		fontSize: 16,
		textAlign: 'center',
		fontFamily: 'Poppins-SemiBold',
	},
	space: {
		marginBottom: '15px',
	},
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		marginBottom: 10,
	},
	table: {
		display: 'flex',
		flexDirection: 'column',
		borderWidth: 1,
		borderColor: '#d3d3d3',
		marginBottom: 10,
	},
	tableRow: {
		flexDirection: 'row',
		borderBottomWidth: 1,
		borderBottomColor: '#d3d3d3',
	},
	tableCell: {
		padding: 5,
		flex: 1,
		borderRightWidth: 1,
		borderRightColor: '#d3d3d3',
	},
	header: {
		backgroundColor: '#f3f3f3',
	},
});

const AnwserColumnsPDF: React.FC<{ row: UserComplaint }> = ({ row }) => {
	const getAnswersById = (
		answers: Answer[],
		questionType: string,
		options: MultipleChoiceOptionPDF[],
		questionId: number
	): string => {
		const findAnswer = answers.find((item) => item.question_id === questionId);
		if (!findAnswer) return '';
		if (questionType !== 'MULTIPLE') {
			return findAnswer.content;
		}

		const choiceOptions = findAnswer.multiple_choice_responses
			.filter((resp) => !!resp.answer)
			.map((data) => data.multiple_id);
		const content = options.filter((opt) => choiceOptions.includes(opt.id));
		return content.map((c) => `${c.brand} ${c.complement}`).join('');
	};

	return (
		<>
			{row.questions?.map((item, index) => {
				const content =
					(row.answers &&
						getAnswersById(
							row.answers,
							item.question_type,
							item.multiple_choice_options,
							item.id
						)) ||
					'';
				return (
					<Text
						key={`answer-${index}`}
						style={[styles.tableCell, styles.text, { flex: 1 }]}
					>
						{content}
					</Text>
				);
			})}
		</>
	);
};

export const PDFDemandLetter = ({ tableData, caseName }: Props) => {
	const allQuestions = tableData?.users.flatMap((item) => item.questions);
	const uniqueQuestions = Array.from(
		new Map(allQuestions?.map((question) => [question?.id, question])).values() // Remove questions repeated
	);
	return (
		<Document>
			<Page size={'A4'} style={styles.page}>
				{/* Title */}
				<View style={styles.space}>
					<Text style={styles.title}>{`${caseName}`}</Text>
				</View>
				{tableData && (
					<View style={styles.table}>
						{/* Header */}
						<View style={[styles.tableRow, styles.header, styles.subTitle]}>
							<View style={{ width: '130px' }}>
								<Text style={[styles.tableCell]}>Name</Text>
							</View>
							<View style={{ width: '130px' }}>
								<Text style={[styles.tableCell]}>State</Text>
							</View>
							<Text style={[styles.tableCell, { textAlign: 'center' }]}>
								All Questions from Claim Form
							</Text>
						</View>
						<View style={[styles.tableRow, styles.header, styles.subTitle]}>
							<View style={{ width: '130px' }}>
								<Text style={[styles.tableCell]}></Text>
							</View>
							<View style={{ width: '130px' }}>
								<Text style={[styles.tableCell]}></Text>
							</View>
							{uniqueQuestions?.map((question, index) => (
								<Text
									key={`question-${index}`}
									style={[styles.tableCell, styles.text, { flex: 1 }]}
								>
									{question?.content}
								</Text>
							))}
						</View>
						{/* Body */}
						{tableData.users.map((row, index) => (
							<View key={`row-${index}`} style={styles.tableRow}>
								<View style={{ width: '130px' }}>
									<Text style={[styles.tableCell, styles.text]}>
										{row.first_name + ' ' + row.last_name}
									</Text>
								</View>
								<View style={{ width: '130px' }}>
									<Text style={[styles.tableCell, styles.text]}>
										{row.state}
									</Text>
								</View>
								<AnwserColumnsPDF row={row} />
							</View>
						))}
					</View>
				)}
			</Page>
		</Document>
	);
};
